import * as React from "react"
import { graphql } from "gatsby"
import {
  useTranslation,
  Trans,
  Link,
  useI18next,
} from "@zestia/gatsby-plugin-react-i18next"
import useHeadTranslation from "@/hooks/use-head-translation"

import MainLayout from "@/layouts/MainLayout"
import IntegrationMarketplaceHeader from "@/components/IntegrationMarketplaceHeader"
import IntegrationCarousel from "@/components/IntegrationCarousel"

import SharedHead from "@/components/SharedHead"
import IntegrationNavigation from "@/components/IntegrationNavigation"
import findImage from "@/utils/find-image"
import { buildRequestBodyForExternalAuth, createSession } from "@/utils/signup"
import { appServerHost } from "@/utils/app-server"
import Accordion from "@/components/Accordion"
import AccordionItem from "@/components/AccordionItem"
import { gtmTracking } from "@/utils/gtm-tracking"

export const Head = props => {
  const { t } = useHeadTranslation(props)

  return (
    <SharedHead
      {...props}
      title={t("xero-crm.meta-title")}
      description={t("xero-crm.meta-description")}
    />
  )
}

const XeroCRM = ({ data, location }) => {
  const [signupLoading, setSignupLoading] = React.useState(false)
  const { t } = useTranslation()
  const { language } = useI18next()

  const onClick = () => {
    gtmTracking("CTA Click", "Try Free", "Xero Marketplace")()
    setSignupLoading(true)
    createSession(
      location,
      buildRequestBodyForExternalAuth(
        "Xero",
        "growth",
        "xeromarketplace",
        language,
      ),
    )
      .then(data => {
        let sessionId = data.signup ? data.signup.id : null

        if (sessionId) {
          setSignupLoading(false)
          window.location =
            "https://app." +
            appServerHost(location) +
            "/oauth2/signup/xero?session_id=" +
            sessionId
        } else {
          setSignupLoading(false)
          throw new Error("Unable to process your request")
        }
      })
      .catch(error => {
        setSignupLoading(false)
        console.error(error)
      })
  }

  return (
    <MainLayout
      showNav={false}
      trackingData={{
        content_category: "integrations",
      }}
    >
      <IntegrationNavigation availableSlugs={null} />
      <section className="pt-20 container-fluid row">
        <div className="col-xs-12">
          <div className="mb-10">
            <Link
              to="/integrations/"
              className="text-link text-link--arrow-before mb-10"
            >
              {t("integrations.navigation.back-to-all")}
            </Link>
          </div>
          <IntegrationMarketplaceHeader
            image={"xero.svg"}
            title={t("xero-crm.header.title")}
            description={t("xero-crm.header.description")}
            iconColour={"1fc0e7"}
            supportButtonUrl={"/support/integrations/xero-integration/"}
            supportButtonText={t("xero-crm.header.how-to-install")}
            supportButtonLabel={t("xero-crm.header.for-capsule-users")}
            marketplaceButtonText={t("xero-crm.header.start-trial")}
            marketplaceButtonLabel={t("xero-crm.header.via-marketplace")}
            marketplaceButtonOnClick={onClick}
            marketplaceButtonLoading={signupLoading}
          />
          <IntegrationCarousel
            data={[
              {
                image: {
                  src: findImage(data.allFile, "xero-export-modal"),
                  alt: t("xero-crm.carousel.image-one-alt"),
                },
              },
              {
                image: {
                  src: findImage(data.allFile, "xero-invoice-list"),
                  alt: t("xero-crm.carousel.image-two-alt"),
                },
              },
            ]}
          />

          <div className={`col-xs-12 row`}>
            <div className="col-md-7">
              <p className="p--lg">
                <Trans
                  i18nKey="xero-crm.body.paragraphOne"
                  components={{
                    Link: (
                      <Link
                        to="/what-is-a-crm"
                        className="text-link text-link--bare"
                      />
                    ),
                  }}
                />
              </p>

              <p>{t("xero-crm.body.paragraphTwo")}</p>

              <p>{t("xero-crm.body.paragraphThree")}</p>

              <p>{t("xero-crm.body.paragraphFour")}</p>

              <p>{t("xero-crm.body.paragraphFive")}</p>
            </div>

            <aside className="integration-page__aside col-md-4 col-md-offset-1">
              <div>
                <p className="mb-1 p--sm">{t("integrations.available-on")}</p>
                <p className="mb-6">
                  <strong>{t("xero-crm.available-on")}</strong>
                </p>
              </div>

              <p className="mb-1 p--sm">{t("integration-layout.category")}</p>

              <Link
                to={`/integrations/?category=invoicing-and-accounting`}
                className="text-link text-link--arrow"
              >
                <strong>
                  {t("integrations.categories.invoicing-and-accounting")}
                </strong>
              </Link>
              <div>
                <p className="mb-1 mt-6 p--sm">
                  {t("integrations.requirements")}
                </p>
                <a
                  href={"https://xero5440.partnerlinks.io/jme7lj"}
                  className="mb-6 text-link text-link--arrow"
                >
                  <strong>{t("xero-crm.requirements")}</strong>
                </a>
              </div>
            </aside>
          </div>

          <Accordion faq className="mt-20" title={t("common.faq")}>
            <AccordionItem faqKey="xero-crm.faqs.one">
              <p>{t("xero-crm.faqs.one.answer")}</p>
            </AccordionItem>

            <AccordionItem faqKey="xero-crm.faqs.two">
              <p>{t("xero-crm.faqs.two.answer")}</p>
            </AccordionItem>

            <AccordionItem faqKey="xero-crm.faqs.three">
              <p>{t("xero-crm.faqs.three.answer")}</p>
            </AccordionItem>

            <AccordionItem faqKey="xero-crm.faqs.four">
              <p>{t("xero-crm.faqs.four.answer")}</p>
            </AccordionItem>

            <AccordionItem faqKey="xero-crm.faqs.five">
              <p>{t("xero-crm.faqs.five.answer")}</p>
            </AccordionItem>

            <AccordionItem faqKey="xero-crm.faqs.six">
              <p>{t("xero-crm.faqs.six.answer")}</p>
            </AccordionItem>

            <AccordionItem faqKey="xero-crm.faqs.seven">
              <p>{t("xero-crm.faqs.seven.answer")}</p>
            </AccordionItem>

            <AccordionItem faqKey="xero-crm.faqs.eight">
              <p>{t("xero-crm.faqs.eight.answer")}</p>
            </AccordionItem>

            <AccordionItem faqKey="xero-crm.faqs.nine">
              <p>{t("xero-crm.faqs.nine.answer")}</p>
            </AccordionItem>

            <AccordionItem faqKey="xero-crm.faqs.ten">
              <p>{t("xero-crm.faqs.ten.answer")}</p>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
    </MainLayout>
  )
}

export const query = graphql`
  query {
    ...Locales
    allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativeDirectory: { eq: "integrations/xero" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData(placeholder: BLURRED, formats: AUTO)
          }
        }
      }
    }
  }
`
export default XeroCRM
